import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Layout } from 'react-grid-layout'
import { useLocation, useNavigate } from 'react-router-dom'
import { Body1, Caption1, Heading2, Heading3 } from '@/components/ui/typography'
import { buildThreePerRowLayout, normalizeLayouts } from '@/routes/Dashboard/utils'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { cn } from 'lib/utils'
import { Check, Edit3, Filter, Plus, Settings } from 'lucide-react'
import { AuthSessionContext } from 'utils/AuthContext'

import { CreateLayoutModal } from './components/CreateLayoutModal'
import { DashboardGrid } from './components/DashboardGrid'
import { EditLayoutMetadataModal } from './components/EditLayoutMetadataModal'
import { LayoutSelector } from './components/LayoutSelector'
import { WidgetPalette } from './components/WidgetPallete'
import { MAX_CUSTOM_LAYOUTS, Widget, WidgetType } from './types'
import { useDashboard } from './useDashboard'

interface DashboardPageProps {
  filters: {
    introducingBrokerIds?: string
    countryIds?: string
    customDateRange?: [string, string]
    intervalType?: number
  } | null
  onOpenFilters: () => void
  filtersOpen: boolean
}
const TEMP_LAYOUT_ID = 'temp'
export const DashboardPage = ({ filters, onOpenFilters, filtersOpen }: DashboardPageProps) => {
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const [rglLayouts, setRglLayouts] = useState<Layout[]>([])

  const [tempLayout, setTempLayout] = useState<{
    name: string
    description: string
    widgets: Widget[]
  } | null>(null)

  const [localEdits, setLocalEdits] = useState<{
    layoutId: string
    widgets: Widget[]
    name?: string
    description?: string
  } | null>(null)

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [layoutToDeleteId, setLayoutToDeleteId] = useState<string | null>(null)

  if (!auth) return <span>Loading...</span>
  const {
    layouts,
    availableWidgets,
    isLoading,
    error,
    createLayout,
    updateLayout,
    deleteLayout,
    refreshLayouts,
  } = useDashboard(auth.user.id)

  const navigate = useNavigate()
  const location = useLocation()

  const [currentLayoutId, setCurrentLayoutId] = useState<string>('')
  const [isEditMode, setIsEditMode] = useState(false)
  const [showWidgetPalette, setShowWidgetPalette] = useState(false)
  const [showCreateLayoutModal, setShowCreateLayoutModal] = useState(false)
  const [showEditMetadataModal, setShowEditMetadataModal] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingLayoutId, setPendingLayoutId] = useState<string | null>(null)
  const getStorageKey = (userId: string) => `ib-dashboard-default-layout-id-${userId}`

  useEffect(() => {
    if (error && location.pathname !== '/dashboard/error') {
      navigate('error', {
        state: {
          layoutId: currentLayoutId,
        },
      })
    }
  }, [error, currentLayoutId, navigate, location.pathname])

  useEffect(() => {
    if (!layouts.length) return
    if (tempLayout) return

    const savedLayoutId = localStorage.getItem(getStorageKey(auth.user.id))
    const layoutExists = layouts.some((l) => l.id === savedLayoutId)
    const currentExists = layouts.some((l) => l.id === currentLayoutId)

    if (currentExists) return

    if (savedLayoutId && layoutExists) {
      setCurrentLayoutId(savedLayoutId)
    } else {
      setCurrentLayoutId(layouts[0].id)
    }
  }, [layouts, auth.user.id, tempLayout, currentLayoutId])

  const currentLayout = React.useMemo(() => {
    if (tempLayout) {
      return {
        id: 'temp',
        name: tempLayout.name,
        description: tempLayout.description,
        isPreset: false,
        widgets: tempLayout.widgets,
      }
    }

    const foundLayout = layouts.find((l) => l.id === currentLayoutId)

    if (localEdits && localEdits.layoutId === currentLayoutId && foundLayout) {
      return {
        ...foundLayout,
        name: localEdits.name || foundLayout.name,
        description: localEdits.description || foundLayout.description,
        widgets: localEdits.widgets,
      }
    }

    return foundLayout || (currentLayoutId ? null : layouts[0])
  }, [tempLayout, localEdits, layouts, currentLayoutId])

  const widgets = currentLayout?.widgets || []
  const layoutConfigs = React.useMemo(() => {
    if (currentLayout?.isPreset) {
      return buildThreePerRowLayout(widgets)
    }

    return widgets.map((w) => w.layout)
  }, [widgets, currentLayout])
  useEffect(() => {
    if (currentLayout) {
      setRglLayouts(
        normalizeLayouts(
          currentLayout.widgets.map((w) => ({
            i: w.id,
            x: w.layout.x,
            y: w.layout.y,
            w: w.layout.w,
            h: w.layout.h,
          }))
        )
      )
    }
  }, [currentLayout])

  const handleLayoutChange = useCallback(
    (newLayouts: Layout[]) => {
      setRglLayouts(newLayouts)

      if (!isEditMode || !currentLayout || currentLayout.isPreset) {
        return
      }

      const updatedWidgets = currentLayout.widgets.map((widget) => {
        const nl = newLayouts.find((l) => l.i === widget.id)
        if (!nl) return widget

        return {
          ...widget,
          layout: {
            ...widget.layout,
            x: nl.x,
            y: nl.y,
            w: nl.w,
            h: nl.h,
          },
        }
      })

      if (tempLayout) {
        setTempLayout({
          ...tempLayout,
          widgets: updatedWidgets,
        })
        return
      }

      setLocalEdits({
        layoutId: currentLayoutId,
        widgets: updatedWidgets,
      })
    },
    [isEditMode, currentLayout, currentLayoutId, tempLayout]
  )

  const handleRemoveWidgetByType = useCallback(
    async (widgetType: WidgetType) => {
      if (!currentLayout || currentLayout.isPreset) {
        return
      }

      const updatedWidgets = currentLayout.widgets.filter((widget) => widget.type !== widgetType)

      if (tempLayout) {
        setTempLayout({
          ...tempLayout,
          widgets: updatedWidgets,
        })
        return
      }

      setLocalEdits({
        layoutId: currentLayoutId,
        widgets: updatedWidgets,
      })
    },
    [currentLayout, currentLayoutId, tempLayout]
  )
  const handleRemoveWidgetById = useCallback(
    (widgetId: string) => {
      if (!currentLayout || currentLayout.isPreset) {
        return
      }

      const updatedWidgets = currentLayout.widgets.filter((widget) => widget.id !== widgetId)

      if (tempLayout) {
        setTempLayout({
          ...tempLayout,
          widgets: updatedWidgets,
        })
        return
      }

      setLocalEdits({
        layoutId: currentLayoutId,
        widgets: updatedWidgets,
      })
    },
    [currentLayout, currentLayoutId, tempLayout]
  )

  const handleAddWidget = useCallback(
    async (widget: Widget) => {
      if (!currentLayout || currentLayout.isPreset) {
        return
      }
      const widgetWidth = widget.size.defaultWidth
      const widgetHeight = widget.size.defaultHeight
      const occupiedPositions = widgets.map((w) => ({
        x: w.layout.x,
        y: w.layout.y,
        w: w.layout.w,
        h: w.layout.h,
      }))

      let newX = 0
      let newY = 0
      let found = false

      for (let row = 0; row < 10 && !found; row++) {
        for (let col = 0; col <= 12 - widgetWidth && !found; col += 4) {
          const yPosition = row * widgetHeight

          const isAvailable = !occupiedPositions.some((pos) => {
            return !(
              col >= pos.x + pos.w ||
              col + widgetWidth <= pos.x ||
              yPosition >= pos.y + pos.h ||
              yPosition + widgetHeight <= pos.y
            )
          })

          if (isAvailable) {
            newX = col
            newY = yPosition
            found = true
          }
        }
      }
      if (!found) {
        newX = 0
        newY = Infinity
      }

      const newWidget: Widget = {
        ...widget,
        layout: {
          i: widget.id,
          x: newX,
          y: newY,
          w: widget.size.defaultWidth,
          h: widget.size.defaultHeight,
          minW: widget.size.minWidth,
          minH: widget.size.minHeight,
        },
      }

      const updatedWidgets = [...currentLayout.widgets, newWidget]

      if (tempLayout) {
        setTempLayout({
          ...tempLayout,
          widgets: updatedWidgets,
        })
        return
      }

      setLocalEdits({
        layoutId: currentLayoutId,
        widgets: updatedWidgets,
      })
    },
    [currentLayout, currentLayoutId, widgets, tempLayout]
  )

  const handleCreateLayout = async (name: string, description: string, presetId?: string) => {
    const customLayoutCount = layouts.filter((l) => !l.isPreset).length
    if (customLayoutCount >= MAX_CUSTOM_LAYOUTS) {
      return
    }

    let widgets: Widget[] = []

    if (presetId) {
      const presetLayout = layouts.find((l) => l.id === presetId && l.isPreset)

      if (presetLayout) {
        const clonedWidgets = presetLayout.widgets.map((widget) => ({
          ...widget,
          id: crypto.randomUUID(),
        }))

        const newLayouts = buildThreePerRowLayout(clonedWidgets)

        widgets = clonedWidgets.map((widget, index) => {
          const baseLayout = newLayouts[index]

          return {
            ...widget,
            layout: {
              ...baseLayout,
              i: widget.id,
              minW: widget.size.minWidth,
              minH: widget.size.minHeight,
            },
          }
        })
      }
    }

    setTempLayout({
      name,
      description: description || 'Custom dashboard layout',
      widgets,
    })

    setShowCreateLayoutModal(false)
    setIsEditMode(true)
    setCurrentLayoutId(TEMP_LAYOUT_ID)
  }

  const handleDeleteLayout = (layoutId: string) => {
    if (!layoutId) {
      console.error('Cannot delete layout: layoutId is undefined')
      return
    }

    const layoutToDelete = layouts.find((l) => l.id === layoutId)

    if (!layoutToDelete || layoutToDelete.isPreset) {
      return
    }

    setLayoutToDeleteId(layoutId)
    setShowDeleteConfirmation(true)
    setIsEditMode(false)
  }

  const confirmDeleteLayout = async () => {
    if (!layoutToDeleteId) return

    const storageKey = getStorageKey(auth.user.id)
    const savedLayoutId = localStorage.getItem(storageKey)

    if (layoutToDeleteId === TEMP_LAYOUT_ID) {
      setTempLayout(null)
      setIsEditMode(false)

      if (layouts.length > 0) {
        const fallbackId = layouts[0].id
        setCurrentLayoutId(fallbackId)
        localStorage.setItem(storageKey, fallbackId)
      } else {
        setCurrentLayoutId('')
        localStorage.removeItem(storageKey)
      }

      setShowDeleteConfirmation(false)
      setLayoutToDeleteId(null)
      setToast(successToast('Custom layout deleted successfully'))
      return
    }

    try {
      await deleteLayout(layoutToDeleteId)

      if (savedLayoutId === layoutToDeleteId) {
        localStorage.removeItem(storageKey)
      }

      if (currentLayoutId === layoutToDeleteId) {
        const firstLayout = layouts.find((l) => l.id !== layoutToDeleteId)

        if (firstLayout) {
          setCurrentLayoutId(firstLayout.id)
          localStorage.setItem(storageKey, firstLayout.id)
        } else {
          setCurrentLayoutId('')
          localStorage.removeItem(storageKey)
        }
      }

      setShowDeleteConfirmation(false)
      setLayoutToDeleteId(null)
      setToast(successToast('Custom layout deleted successfully'))
    } catch (err) {
      console.error('Failed to delete layout:', err)
      setShowDeleteConfirmation(false)
      setLayoutToDeleteId(null)
    }
  }

  const cancelDeleteLayout = () => {
    setShowDeleteConfirmation(false)
    setLayoutToDeleteId(null)
    setToast(successToast('Custom layout was not deleted'))
  }

  const customLayoutCount = layouts.filter((l) => !l.isPreset).length
  const canAddMoreLayouts = customLayoutCount < MAX_CUSTOM_LAYOUTS

  const layoutsWithTemp = React.useMemo(() => {
    if (tempLayout) {
      return [
        ...layouts,
        {
          id: 'temp',
          name: tempLayout.name,
          description: tempLayout.description,
          isPreset: false,
          widgets: tempLayout.widgets,
        },
      ]
    }
    return layouts
  }, [layouts, tempLayout])

  const handleEditLayoutMetadata = (name: string, description: string) => {
    if (tempLayout) {
      setTempLayout({
        ...tempLayout,
        name,
        description,
      })
    } else if (currentLayout && !currentLayout.isPreset) {
      setLocalEdits({
        layoutId: currentLayoutId,
        widgets: localEdits?.widgets || currentLayout.widgets,
        name,
        description,
      })
    }
  }
  const switchLayout = (layoutId: string) => {
    if (tempLayout && layoutId !== TEMP_LAYOUT_ID) {
      setTempLayout(null)
    }

    setLocalEdits(null)
    setIsEditMode(false)

    setCurrentLayoutId(layoutId)
    localStorage.setItem(getStorageKey(auth.user.id), layoutId)
  }
  const handleLayoutSelect = (layoutId: string) => {
    if (!layoutId) return

    if (isEditMode && layoutId !== currentLayoutId) {
      setPendingLayoutId(layoutId)
      setShowUnsavedModal(true)
      return
    }

    switchLayout(layoutId)
  }

  const handleToggleEditMode = async () => {
    if (isEditMode) {
      if (isEditMode) {
        if (tempLayout) {
          try {
            const newLayoutId = await createLayout(
              tempLayout.name,
              tempLayout.description,
              tempLayout.widgets,
              rglLayouts
            )

            setTempLayout(null)
            setIsEditMode(false)

            localStorage.setItem(getStorageKey(auth.user.id), newLayoutId)

            await refreshLayouts()

            setCurrentLayoutId(newLayoutId)
            setToast(successToast('Custom layout created successfully'))
          } catch (err) {
            console.error('Failed to save layout:', err)
          }
          return
        }
      }
      if (localEdits && currentLayout) {
        try {
          await updateLayout(
            localEdits.layoutId,
            localEdits.name || currentLayout.name,
            localEdits.description || currentLayout.description,
            localEdits.widgets,
            rglLayouts
          )
          setLocalEdits(null)
          setIsEditMode(false)
          setToast(successToast('Custom layout updated successfully'))
        } catch (err) {
          console.error('❌ [handleToggleEditMode] Failed to save local edits:', err)
        }
        return
      }
      setToast(successToast('Custom layout updated successfully'))

      setIsEditMode(false)
    } else {
      setIsEditMode(true)
    }
  }
  const handleConfirmSaveAndSwitch = async () => {
    if (!pendingLayoutId) return

    try {
      if (tempLayout) {
        await createLayout(tempLayout.name, tempLayout.description, tempLayout.widgets, rglLayouts)

        await refreshLayouts()
        setTempLayout(null)
      } else if (localEdits && currentLayout) {
        await updateLayout(
          localEdits.layoutId,
          localEdits.name || currentLayout.name,
          localEdits.description || currentLayout.description,
          localEdits.widgets,
          rglLayouts
        )

        setLocalEdits(null)
      }

      switchLayout(pendingLayoutId)
    } catch (err) {
      console.error(err)
    }

    setShowUnsavedModal(false)
    setPendingLayoutId(null)
  }
  const handleDiscardAndSwitch = () => {
    if (!pendingLayoutId) return

    setTempLayout(null)
    setLocalEdits(null)
    setIsEditMode(false)

    switchLayout(pendingLayoutId)

    setShowUnsavedModal(false)
    setPendingLayoutId(null)
  }
  return (
    <div className='flex min-h-full flex-col'>
      <Loading isLoading={isLoading}>
        <div className='flex shrink-0 items-center justify-between border-b border-gray-200  px-6 py-4 shadow-sm'>
          <div className='flex items-end gap-4'>
            <Heading2 bold>IB Dashboard</Heading2>
            <Body1 className='rounded-full bg-purple-100 px-4 py-2 text-sm font-medium text-purple-700'>
              {widgets.length} widgets active
            </Body1>
          </div>

          <div className='flex items-center gap-3'>
            <div style={{ pointerEvents: filtersOpen ? 'none' : 'auto' }} className='inline-block'>
              <button
                onClick={onOpenFilters}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all',
                  filters &&
                    (filters.introducingBrokerIds || filters.countryIds || filters.customDateRange)
                    ? 'border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100'
                    : 'border-gray-300  hover:border-gray-400 hover:bg-gray-50'
                )}
              >
                <Filter className='h-4 w-4' />
                <span>Filters</span>
                {filters &&
                  (filters.introducingBrokerIds ||
                    filters.countryIds ||
                    filters.customDateRange) && (
                    <span className='flex min-w-6 items-center justify-center rounded-full bg-purple-600 px-2 py-0.5 text-xs font-semibold text-white'>
                      {(filters.introducingBrokerIds?.split(',').length || 0) +
                        (filters.countryIds?.split(',').length || 0) +
                        (filters.customDateRange ? 1 : 0)}
                    </span>
                  )}
              </button>
            </div>

            <LayoutSelector
              layouts={layoutsWithTemp}
              currentLayoutId={currentLayoutId}
              onLayoutSelect={handleLayoutSelect}
              onCreateNew={() => setShowCreateLayoutModal(true)}
              onDeleteLayout={handleDeleteLayout}
              canAddMore={canAddMoreLayouts}
            />

            {!currentLayout?.isPreset && (
              <button
                onClick={handleToggleEditMode}
                className={cn(
                  'flex items-center gap-2 rounded-lg border border-gray-300  px-4 py-2.5 text-sm font-medium transition-all hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700',
                  isEditMode && 'border-purple-500 bg-purple-50 text-purple-700'
                )}
              >
                {isEditMode ? (
                  <>
                    <Check className='h-4 w-4' />
                    <span>Done</span>
                  </>
                ) : (
                  <>
                    <Edit3 className='h-4 w-4' />
                    <span>Edit</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {isEditMode && (
          <div className='flex shrink-0 items-center justify-between border-b border-amber-200 bg-amber-50 px-6 py-3'>
            <div className='flex items-center gap-3'>
              <span className='text-2xl'>💡</span>
              <p className='text-sm '>
                Edit Mode: Drag widgets to reorder, resize by dragging corners, or remove with the ×
                button
              </p>
            </div>
            <div className='flex items-center gap-2'>
              <button
                onClick={() => setShowEditMetadataModal(true)}
                className='flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium  transition-colors '
              >
                <Settings className='h-4 w-4' />
                <span>Settings</span>
              </button>
              <button
                onClick={() => setShowWidgetPalette(true)}
                className='flex items-center gap-2 rounded-lg border border-purple-300 bg-purple-600 px-3 py-1.5 text-sm font-medium  transition-colors hover:bg-purple-700'
              >
                <Plus className='h-4 w-4' />
                <span>Manage Widgets</span>
              </button>
            </div>
          </div>
        )}

        <div className='relative flex-1 overflow-y-auto min-h-0  hide-scrollbar'>
          {widgets.length === 0 && !isEditMode ? (
            <div className='flex h-full flex-col items-center justify-center p-12'>
              <div className='text-center'>
                <div className='mb-4 text-6xl opacity-50'>📊</div>

                <Heading3 semibold className='mb-2'>
                  No widgets yet
                </Heading3>

                <Caption1 className='text-gray-600'>
                  Click "Edit" to start adding widgets to your dashboard
                </Caption1>
              </div>
            </div>
          ) : (
            <DashboardGrid
              widgets={widgets}
              layouts={layoutConfigs}
              onLayoutChange={handleLayoutChange}
              isEditMode={isEditMode}
              filters={filters}
              onRemove={handleRemoveWidgetById}
            />
          )}
        </div>
        <WidgetPalette
          isOpen={showWidgetPalette}
          onClose={() => setShowWidgetPalette(false)}
          onAddWidget={handleAddWidget}
          onRemoveWidget={handleRemoveWidgetByType}
          existingWidgets={widgets}
          availableWidgets={availableWidgets}
        />
        <CreateLayoutModal
          isOpen={showCreateLayoutModal}
          onClose={() => setShowCreateLayoutModal(false)}
          onCreate={handleCreateLayout}
          presetLayouts={layouts.filter((l) => l.isPreset)}
        />
        <EditLayoutMetadataModal
          isOpen={showEditMetadataModal}
          onClose={() => setShowEditMetadataModal(false)}
          onSave={handleEditLayoutMetadata}
          currentName={currentLayout?.name || ''}
          currentDescription={currentLayout?.description || ''}
        />
        <ConfirmationModal
          visible={showDeleteConfirmation}
          onYes={confirmDeleteLayout}
          onCancel={cancelDeleteLayout}
          yesButtonText='Yes'
          cancelButtonText='No'
          contentText='Do you want to delete custom layout?'
        />
        <ConfirmationModal
          visible={showUnsavedModal}
          onYes={handleConfirmSaveAndSwitch}
          onCancel={handleDiscardAndSwitch}
          onClose={() => {
            setShowUnsavedModal(false)
            setPendingLayoutId(null)
          }}
          yesButtonText='Yes'
          cancelButtonText='No'
          contentText='Do you want to save changes?'
        />
      </Loading>
    </div>
  )
}
